const CITY_BUCKET_DEG = 0.15;

const pointInRing = (lng, lat, ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect =
      ((yi > lat) !== (yj > lat)) &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi || Number.EPSILON) + xi;
    if (intersect) {
      inside = !inside;
    }
  }
  return inside;
};

const pointInPolygonCoords = (lng, lat, coordinates) => {
  if (!coordinates?.length) {
    return false;
  }
  if (!pointInRing(lng, lat, coordinates[0])) {
    return false;
  }
  for (let i = 1; i < coordinates.length; i += 1) {
    if (pointInRing(lng, lat, coordinates[i])) {
      return false;
    }
  }
  return true;
};

export const pointInGeometry = (lng, lat, geometry) => {
  if (!geometry) {
    return false;
  }
  if (geometry.type === "Polygon") {
    return pointInPolygonCoords(lng, lat, geometry.coordinates);
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((polygon) => pointInPolygonCoords(lng, lat, polygon));
  }
  return false;
};

export const normalizeCity = (address) => {
  if (!address || typeof address !== "string") {
    return "";
  }
  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) {
    return "";
  }
  let candidate = parts[0];
  if ((candidate.length > 40 || /^\d+$/.test(candidate)) && parts[1]) {
    candidate = parts[1];
  }
  return candidate;
};

export const cityKeyFor = (hotel) => {
  const normalized = normalizeCity(hotel.city);
  if (normalized) {
    return normalized.toLowerCase();
  }
  const lat = Math.round(hotel.lat / CITY_BUCKET_DEG) * CITY_BUCKET_DEG;
  const lng = Math.round(hotel.lng / CITY_BUCKET_DEG) * CITY_BUCKET_DEG;
  return `geo:${lat},${lng}`;
};

export const cityLabelFor = (hotel, cityKey) => {
  const normalized = normalizeCity(hotel.city);
  if (normalized) {
    return normalized;
  }
  return cityKey.startsWith("geo:") ? "Nearby hotels" : cityKey;
};

export const uniqueChainIds = (hotels = []) =>
  [...new Set(hotels.map((hotel) => hotel.chainId).filter(Boolean))];

// Here: `hotelType === "HMS"` → HMS; any other `hotelType` → third-party brand (IHCL, Leela, …).

export const isHmsMapHotel = (hotel) => hotel?.hotelType === "HMS";

const normalizeBrandKey = (value) => String(value ?? "").trim().toLowerCase();

/** Third-party brand on map rows — primary field is `hotelType` (not HMS). */
export const mapHotelBrandCandidates = (hotel) => {
  if (isHmsMapHotel(hotel)) {
    return [];
  }
  const raw = [hotel?.hotelType, hotel?.hotelBrand, hotel?.brand];
  const seen = new Set();
  const normalized = [];
  raw.forEach((value) => {
    const key = normalizeBrandKey(value);
    if (!key || key === "hms" || key === "third party" || seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(key);
  });
  return normalized;
};

/** Popup / marker type line: HMS hotels vs brand name from `hotelType`. */
export const mapHotelTypeLabel = (hotel) => {
  if (isHmsMapHotel(hotel)) {
    return "HMS";
  }
  const brandFromType = String(hotel?.hotelType ?? "").trim();
  return brandFromType || "Third Party";
};

export const hotelMatchesBrandFilter = (hotel, brandFilter, brandMatchAliases = []) => {
  const targets = [brandFilter, ...(brandMatchAliases || [])]
    .map(normalizeBrandKey)
    .filter(Boolean);
  if (!targets.length) {
    return true;
  }
  const hotelBrands = mapHotelBrandCandidates(hotel);
  return targets.some((target) => hotelBrands.includes(target));
};

export const hotelStats = (hotels = []) => {
  const hms = hotels.filter((hotel) => isHmsMapHotel(hotel)).length;
  return {
    hotels: hotels.length,
    hms,
    thirdParty: hotels.length - hms,
    stbCount: hotels.reduce((sum, hotel) => sum + (Number(hotel.STBcount) || 0), 0),
  };
};

const mapHotelBrandBucket = (hotel) => {
  if (isHmsMapHotel(hotel)) {
    return "HMS";
  }
  const label = mapHotelTypeLabel(hotel);
  return label === "Third Party" ? "Other" : label;
};

/** Per-brand hotel counts for map tooltips (HMS first, then A–Z). */
export const hotelBrandCountLines = (hotels = []) => {
  const counts = new Map();
  hotels.forEach((hotel) => {
    const label = mapHotelBrandBucket(hotel);
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return [...counts.entries()].sort(([a], [b]) => {
    if (a === "HMS") {
      return -1;
    }
    if (b === "HMS") {
      return 1;
    }
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
};

/** India map state / pin hover (amCharts formatted text: `[bold]` title). */
export const stateMapTooltipText = (stateName, hotels = []) => {
  const stats = hotelStats(hotels);
  const brandBlock = hotelBrandCountLines(hotels)
    .map(([label, count]) => `${label} : ${count}`)
    .join("\n");
  const lines = [`[fontSize:13px;fontWeight:700]${stateName}[/]`];
  if (brandBlock) {
    lines.push(brandBlock);
  }
  lines.push(`STBs : ${stats.stbCount}`);
  return lines.join("\n");
};

export const averagePoint = (hotels = []) => {
  if (!hotels.length) {
    return { lat: 0, lng: 0 };
  }
  const totals = hotels.reduce(
    (acc, hotel) => {
      acc.lat += Number(hotel.lat) || 0;
      acc.lng += Number(hotel.lng) || 0;
      return acc;
    },
    { lat: 0, lng: 0 }
  );
  return {
    lat: totals.lat / hotels.length,
    lng: totals.lng / hotels.length,
  };
};

export const matchesHotelTypeFilter = (hotel, filterType) => {
  if (!filterType || filterType === "all") {
    return true;
  }
  const isHms = isHmsMapHotel(hotel);
  if (filterType === "HMS") {
    return isHms;
  }
  return !isHms;
};

export const matchesMapHotelFilter = (hotel, filterType, brandFilter, brandMatchAliases = []) => {
  if (!matchesHotelTypeFilter(hotel, filterType)) {
    return false;
  }
  const brand = String(brandFilter ?? "").trim();
  if (!brand || brand === "All" || filterType !== "THIRD_PARTY") {
    return true;
  }
  return hotelMatchesBrandFilter(hotel, brand, brandMatchAliases);
};

export const assignHotelsToStates = (hotels, geojson) => {
  const features = geojson?.features || [];
  return (hotels || []).map((hotel) => {
    const lng = Number(hotel.lng);
    const lat = Number(hotel.lat);
    const feature = features.find((item) => pointInGeometry(lng, lat, item.geometry));
    return {
      ...hotel,
      stateId: feature?.id || feature?.properties?.id || null,
      stateName: feature?.properties?.name || null,
    };
  });
};

export const buildRegionalTree = (hotels) => {
  const states = {};
  hotels.forEach((hotel) => {
    if (!hotel.stateId) {
      return;
    }
    if (!states[hotel.stateId]) {
      states[hotel.stateId] = {
        id: hotel.stateId,
        name: hotel.stateName,
        hotels: [],
        cities: {},
      };
    }
    states[hotel.stateId].hotels.push(hotel);
    const cityKey = cityKeyFor(hotel);
    if (!states[hotel.stateId].cities[cityKey]) {
      states[hotel.stateId].cities[cityKey] = {
        key: cityKey,
        name: cityLabelFor(hotel, cityKey),
        hotels: [],
      };
    }
    states[hotel.stateId].cities[cityKey].hotels.push(hotel);
  });
  return states;
};

export const getStateFeature = (geojson, stateId) =>
  (geojson?.features || []).find(
    (feature) => feature.id === stateId || feature.properties?.id === stateId
  );

export const geometryBounds = (geometry) => {
  let minLng = 180;
  let minLat = 90;
  let maxLng = -180;
  let maxLat = -90;
  const walk = (coords) => {
    if (typeof coords[0] === "number") {
      minLng = Math.min(minLng, coords[0]);
      maxLng = Math.max(maxLng, coords[0]);
      minLat = Math.min(minLat, coords[1]);
      maxLat = Math.max(maxLat, coords[1]);
      return;
    }
    (coords || []).forEach(walk);
  };
  if (geometry?.coordinates) {
    walk(geometry.coordinates);
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};
